import d3 from 'd3';
import _ from 'lodash';

class BaseFacetView {

  constructor(config, $el, margins) {
    // PRIVATE VARS

    this._title = 'Base Chart';
    this._data = null;

    // Elements
    this._div = null;
    this._svg = null;
    this._margins = null;
    this._width = null;
    this._height = null;
    this._spacing = 2;
    this._spacingBg = 2;

    // Colors
    this._upColor = '#677989';
    this._downColor = '#69E9B4';
    this._overColor = '#778999';

    this._selectedUpColor = '#59D9A4';
    this._selectedDownColor = '#69E9B4';
    this._selectedOverColor = '#69E9B4';

    this._upColorBg = '#262A31';
    this._downColorBg = '#141414';
    this._overColorBg = '#1F2124';

    this._selectedUpColorBg = '#141414';
    this._selectedDownColorBg = '#000000';
    this._selectedOverColorBg = '#000000';

    this._axisColor = '#BBC0CE';
    this._axisLabelColor = '#BBC0CE';

    // Chart
    this._xScaleFunction = null;
    this._xValue = null; // data -> value
    this._xScale = null; // value -> display
    this._xMap = null; // data -> display
    this._xExtent = null;
    this._xGrid = null;

    this._yScaleFunction = null;
    this._yValue = null; // data -> value
    this._yScale = null; // value -> display
    this._yMap = null; // data -> display
    this._yExtent = null;
    this._yGrid = null;

    // Selection
    this._mouseDown = null;
    this._selectionMode = null;

    this._interactive = true;
    this._liveUpdate = false;

    this._selection = undefined;
    this._multiSelection = false; // remove?

    this._boundDataChanged = null;
    this._boundSelectionChanged = null;


    console.info('Creating base facetView', config.name);
    this._config = config;
    // this._facet.dataChanged.add(data => this._onCollectionDataChanged(data));
    // this._visibilityClicked = new Signal();

    this._div = d3.select($el[0]);
    this._margins = _.defaults(margins || {}, {
      width: 500,
      top: 30,
      right: 30,
      bottom: 20,
      left: 50,
    });

    this._init();
    // this._updateElements();
  }

  // PUBLIC

  facet(facet) {
    if (!arguments.length) return this._facet;
    if (this._facet) {
      this._facet.dataChanged.remove(this._boundDataChanged);
      this._facet.selectionChanged.remove(this._boundSelectionChanged);
    }
    this._facet = facet;
    if (this._facet) {
      this._boundSelectionChanged = (extent) => this._onSelectionChanged(extent);
      this._facet.selectionChanged.add(this._boundSelectionChanged);
      this._updateSelection(null);
    }
    this.update();
    return this;
  }

  // SIGNALS

  // HELPERS

  // PRIVATE

  // Init

  _init() {
    this._xScaleFunction = d3.scale.linear();
    this._xFormat = d => d;
    this._yFormat = d => d;
    this._xMap = d => this._xScale(this._xValue(d));

    this._yScaleFunction = d3.scale.linear();
    this._yMap = d => this._yScale(this._yValue(d));

    this._initGetSetters();
  }

  _initGetSetters() {
    this.xValue = (value) => this._getSetter(value, '_xValue', this._updateScales);
    this.xScaleFunction = (value) => this._getSetter(value, '_xScaleFunction', this._updateScales);
    this.xAxis = (value) => this._getSetter(value, '_xAxis', this._updateScales);
    this.xFormat = (value) => this._getSetter(value, '_xFormat', this._updateScales);
    this.xLabel = (value) => this._getSetter(value, '_xLabel', this._updateScales);

    this.yValue = (value) => this._getSetter(value, '_yValue', this._updateScales);
    this.yScaleFunction = (value) => this._getSetter(value, '_yScaleFunction', this._updateScales);
    this.yAxis = (value) => this._getSetter(value, '_yAxis', this._updateScales);
    this.yFormat = (value) => this._getSetter(value, '_yFormat', this._updateScales);
    this.yLabel = (value) => this._getSetter(value, '_yLabel', this._updateScales);

    this.spacing = (value) => this._getSetter(value, '_spacing', this._updateScales);
    this.spacingBg = (value) => this._getSetter(value, '_spacingBg', this._updateScales);
    this.limit = (value) => this._getSetter(value, '_limit', this._updateData);
    this.title = (value) => this._getSetter(value, '_title');
    this.liveUpdate = (value) => this._getSetter(value, '_liveUpdate');

    this.selection = (value) => this._getSetter(value, '_selection');
  }

  _getSetter(value, name, fun) {
    if (!arguments.length) {
      return this[name];
    }
    this[name] = value;
    if (fun) fun.call(this);
    return this;
  }

  colors(c) {
    var currentColors = {
      upColor: this._upColor,
      downColor: this._downColor,
      overColor: this._overColor,

      selectedUpColor: this._selectedUpColor,
      selectedDownColor: this._selectedDownColor,
      selectedOverColor: this._selectedOverColor,

      upColorBg: this._upColorBg,
      downColorBg: this._downColorBg,
      overColorBg: this._overColorBg,

      selectedUpColorBg: this._selectedUpColorBg,
      selectedDownColorBg: this._selectedDownColorBg,
      selectedOverColorBg: this._selectedOverColorBg,

      axisColor: this._axisColor,
      axisLabelColor: this._axisLabelColor
    };

    if (!arguments.length) return currentColors;

    var colors = _.defaults(c, currentColors);
    for (var k in colors) this['_' + k] = colors[k];
    this._updateElements();
    return this;
  }

  margins(m) {
    if (!arguments.length) return this._margins;
    this._margins = _.defaults(m, this._margins);
    this._updateElements();
    return this;
  }

  // Elements

  _updateElements() {
    this._updateSize();
    this._updateGroups();
    this._updateScales();
  }

  _updateSize() {
    this._width = parseInt(this._div.style('width')) -
      parseInt(this._div.style('padding-left')) -
      parseInt(this._div.style('padding-right')) -
      this._margins.left -
      this._margins.right;
    this._height = parseInt(this._div.style('height')) -
      parseInt(this._div.style('padding-top')) -
      parseInt(this._div.style('padding-bottom')) -
      this._margins.top -
      this._margins.bottom;
    // this._width = 385;
    // this._height = 300;
  }

  _updateGroups() {
    this._g = this._updateMainGroup();
  }

  _updateMainGroup() {
    var svg = this._div.selectAll('svg').data(['svg']);
    svg.enter().append('svg').append('g');
    svg.attr('width', this._width + this._margins.left + this._margins.right)
      .attr('height', this._height + this._margins.top + this._margins.bottom);

    var g = svg.select('g');
    g.attr('transform', 'translate(' + this._margins.left + ',' + this._margins.top + ')');
    return g;
  }

  // Data

  _updateData() {
    if (!this._facet) return;
    this._data = this._facet.groups(this._limit);
    this._updateScales();
  }

  _drawSelection() {
    throw '_drawSelection must be implemented.';
  }

  // Scales

  _updateScales() {
    if (!this._xValue || !this._yValue || !this._data) return;

    this._xExtent = d3.extent(this._data, this._xValue);
    this._xScale = this._xScaleFunction.domain(this._xExtent).rangeRound([0, this._width]);
    this._xAxis = d3.svg.axis().scale(this._xScale).orient('bottom');
    this._xAxisGroup.call(this._xAxis);
    this._xAxisGroup.attr('transform', 'translate(0,' + (this._height) + ')');

    this._yMax = d3.max(this._data, this._yValue); // TODO: use extent, make overridable
    this._yScale = this._yScaleFunction.domain([0, this._yMax]).rangeRound([this._height, 0]);
    this._yAxis = d3.svg.axis().scale(this._yScale).orient('left');
    this._yAxisGroup.call(this._yAxis);

    this._xGrid = d3.svg.axis().scale(this._xScale).orient('bottom');
    this._xGridGroup.call(this.xGrid);
    this.xGridGroup.attr('transform', 'translate(0' + (this._height) + ')');

    this._drawChart();
  }

  // Data

  _drawChart() {
    throw '_drawChart must be implemented.';
  }

  // Selection

  _select(d, value) {
    throw '_select must be implemented.';
  }

  _isSelected(d) {
    throw '_isSelected must be implemented.';
  }

  _updateSelection(extent) {
    throw '_updateSelection must be implemented.';
  }

  // METHODS

  update() {
    this._updateData();
  }

  redraw() {
    this._updateElements();
  }

  clear() {
    // Implement clearing in subclass
    this._onExtentSelected();
    if (window.DEBUG) console.log(this._title + ': cleared');
  }

  // USER EVENTS HANDLERS

  _onMouseOver(d) {
    throw '_onMouseOver must be implemented.';
  }

  _onMouseOut(d) {
    throw '_onMouseOut must be implemented.';
  }

  _onMouseDown(d) {
    throw '_onMouseDown must be implemented.';
  }

  _onMouseUp() {
    throw '_onMouseUp must be implemented.';
  }

  _onMouseLeave() {
    if (this._mouseDown) {
      d3.select('body').on('mouseup', () => {
        this._onMouseUp();
        d3.select('body').on('mouseup', null);
      });
    }
  }

  _onExtentSelected() {
    throw '_onExtentSelected must be implemented.';
  }

  // MODEL EVENTS HANDLERS

  // _onDataChanged() {
  //   if (window.DEBUG) console.log(this._title + ': data changed');
  //   this._updateData();
  // }

  _onSelectionChanged(extent) {
    this._updateSelection(extent);
    this._drawChart();
  }

}

module.exports = BaseFacetView;



/** WEBPACK FOOTER **
 ** D:/2016/2016 - ISI/grantorino/frontend/../../../../Marco/Dropbox (ISI Foundation)/2016 - ISI/grantorino/frontend/src/js/views/sidebar/archive/facets/BaseFacetView.es6
 **/